import { ClickAwayListener, cx, useAutoResizedTextarea } from "@jugl-web/utils";
import {
  ChangeEvent,
  FC,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { InteractiveContainer } from "../../primitives";

interface TitleBoxProps {
  title: string;
  placeholder?: string;
  emptyPlaceholder?: string;
  isEditable?: boolean;
  maxLength?: number;
  onChange?: (title: string) => void;
  onInternalValueChange?: (title: string) => void;
  validator?: (title: string) => boolean;
}

export const TitleBox: FC<TitleBoxProps> = ({
  title,
  placeholder = "Enter title",
  emptyPlaceholder = "(no title)",
  isEditable = true,
  maxLength,
  onChange,
  onInternalValueChange,
  validator,
}) => {
  const hasTitle = !!title;

  const [internalTitle, setInternalTitle] = useState("");
  const [isEditing, setIsEditing] = useState(!hasTitle);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { resize: resizeTextarea, textareaProps } =
    useAutoResizedTextarea(textareaRef);

  const hasReachedMaxLength =
    typeof maxLength === "number" && internalTitle.length >= maxLength;

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;

    setInternalTitle(value);
    onInternalValueChange?.(value);
  };

  const handleSubmit = () => {
    if (validator && !validator(internalTitle)) {
      return;
    }

    if (internalTitle !== title) {
      onChange?.(internalTitle);
    }

    setIsEditing(false);
  };

  useEffect(() => {
    if (!hasTitle) {
      setIsEditing(true);
    }
  }, [hasTitle]);

  useEffect(() => {
    if (isEditing) {
      setInternalTitle(title);
    }
  }, [title, isEditing]);

  useLayoutEffect(() => {
    if (isEditing) {
      resizeTextarea();
    }
  }, [internalTitle, isEditing, resizeTextarea]);

  // Editing mode
  if (isEditable && (isEditing || !hasTitle)) {
    return (
      <ClickAwayListener onClickAway={handleSubmit}>
        <div className="flex items-start gap-2">
          <textarea
            ref={textareaRef}
            onFocus={(e) => {
              e.target.setSelectionRange(title.length, title.length);
              resizeTextarea();
            }}
            autoFocus
            className="jugl__custom-scrollbar max-h-[300px] w-full resize-none font-secondary text-[22px] font-medium text-dark outline-none placeholder:text-grey-800"
            placeholder={placeholder}
            rows={1}
            maxLength={maxLength}
            value={internalTitle}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            {...textareaProps}
          />
        </div>
        {maxLength !== undefined && (
          <div className="mt-3">
            <span
              className={cx(
                "text-sm leading-2 font-medium",
                hasReachedMaxLength ? "text-tertiary-700" : "text-[#BDBDBD]"
              )}
            >
              {internalTitle.length}/{maxLength}
            </span>
          </div>
        )}
      </ClickAwayListener>
    );
  }

  // Preview mode
  return (
    <InteractiveContainer
      className={cx(
        "transition-color group rounded pr-4 duration-300",
        isEditable && "hover:bg-grey-100"
      )}
      isDisabled={!isEditable}
      onClick={() => {
        setInternalTitle(title);
        setIsEditing(true);
      }}
    >
      <div
        className={cx(
          "flex items-center overflow-hidden wrap-break-word whitespace-pre-wrap transition-transform duration-300",
          isEditable && "group-hover:translate-x-2"
        )}
      >
        <span className="font-secondary text-[22px] font-medium text-dark">
          {hasTitle ? title : emptyPlaceholder}
        </span>
      </div>
    </InteractiveContainer>
  );
};
